/* ServiceCard — Premium service display block */

interface ServiceCardProps {
  data: Record<string, string>
  entityData?: Record<string, unknown>
}

export default function ServiceCard({ data, entityData }: ServiceCardProps) {
  const name = (entityData?.name as string) || data.name || 'Service Name'
  const price = (entityData?.price as number) || data.price || ''
  const duration = (entityData?.duration_minutes as number) || data.duration || ''
  const description = (entityData?.description as string) || data.description || ''

  return (
    <div data-testid="card-service" className="group relative h-full rounded-xl overflow-hidden bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/[0.06] hover:border-white/[0.12] transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_48px_rgba(0,0,0,0.3)]">
      {/* Top accent line */}
      <div className="h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />

      <div className="p-5">
        {/* Name + Price row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <h4 className="text-sm font-semibold text-white/90 leading-snug tracking-tight group-hover:text-white transition-colors">
            {name}
          </h4>
          {price && (
            <span className="text-sm font-bold text-blue-400/80 font-mono whitespace-nowrap tabular-nums">
              ${typeof price === 'number' ? price : price}
            </span>
          )}
        </div>

        {/* Duration chip */}
        {duration && (
          <div className="mb-3">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.04] text-[10px] text-white/30 font-mono">
              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth={1.5} />
                <path strokeWidth={1.5} d="M12 6v6l4 2" />
              </svg>
              {duration} min
            </span>
          </div>
        )}

        {/* Description */}
        {description && (
          <p className="text-xs text-white/30 leading-relaxed line-clamp-2 group-hover:text-white/40 transition-colors">
            {description}
          </p>
        )}
      </div>

      {/* Bottom shimmer on hover */}
      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-blue-500/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
    </div>
  )
}
